import React from 'react';
import { Route } from 'react-router-dom';
import AuthProvider from 'infrastructure/providers/auth-provider';

import { ProtectedRoute } from './utils/protected-route';
import { AccessRouteComponent } from './utils/access-route';

import type {
  RouteObject,
  IndexRouteObject,
  NonIndexRouteObject,
} from 'react-router-dom';
import type { ILayoutRoute } from './types';

export const wrapProtect = (component: React.FC) => {
  return <ProtectedRoute component={component} />;
};

type IWrapAccessIndex = Omit<IndexRouteObject, 'element'> & {
  element: React.FC;
  path: string;
};

type IWrapAccessNonIndex = Omit<NonIndexRouteObject, 'element' | 'children'> & {
  element: React.FC;
  path: string;
  children?: IWrapAccessNonIndex[];
};

export const wrapAccessRoute = (
  route: IWrapAccessIndex | IWrapAccessNonIndex,
): RouteObject => {
  if ('index' in route && route.index === true) {
    const { element, path, ...rest } = route as IWrapAccessIndex;
    return {
      ...rest,
      index: true,
      element: <AccessRouteComponent component={element} path={path} />,
    };
  }

  const { element, path, ...rest } = route as IWrapAccessNonIndex;
  return {
    ...rest,
    index: false,
    children: undefined,
    path,
    element: <AccessRouteComponent component={element} path={path} />,
  };
};
export const wrapAccess = (component: React.FC, path: string) => {
  return <AccessRouteComponent component={component} path={path} />;
};

export const renderRoutes = (mainRoutes: ILayoutRoute[]) => {
  return (
    <Route element={<AuthProvider />}>
      {mainRoutes.map((layoutParams, index) => {
        const {
          layout: Layout,
          routes,
          isProtected = false,
          extraChildren,
        } = layoutParams;
        const layout = isProtected ? wrapProtect(Layout) : <Layout />;

        return (
          <Route key={index} element={layout}>
            {routes.map((routeParams) => {
              const { component: Page, path, isAccessible } = routeParams;
              const page =
                isAccessible ?? isProtected ? wrapAccess(Page, path) : <Page />;
              return <Route key={path} element={page} path={path} />;
            })}
            {extraChildren}
          </Route>
        );
      })}
    </Route>
  );
};
